import { Button } from "@sellar-io/components";
import { ReactElement } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../components/modal/Dialog";
import { useEvents } from "../../../hooks";
import { useOrderFulfill } from "../../../hooks/api/v2/order/v2/useOrderFulfill";
import { OrderPaymentDueFormFragment } from "./OrderPaymentDueFormFragment";
import { OrderGetResponseType } from "@sellar-io/api-v2/types";

type OrderCollectedFormValues = {
  paymentDue: string | null;
};

export function OrderCollectedDialog({
  active,
  onDismiss,
  orderId,
  retailerId,
  invoiceSent,
  invoicePaid,
  isInvoiceCreationStage,
  paymentType,
}: {
  active: boolean;
  onDismiss: () => void;
  retailerId: number;
  orderId: number;
  invoiceSent: boolean;
  invoicePaid: boolean;
  isInvoiceCreationStage: boolean;
  paymentType: OrderGetResponseType["paymentType"];
}): ReactElement {
  const { track } = useEvents();
  const showSellarCreditFeatures = paymentType === "sellarCredit";

  const form = useForm<OrderCollectedFormValues>({
    shouldUseNativeValidation: true,
    defaultValues: {
      paymentDue: null,
    },
  });

  const orderFulfillMutation = useOrderFulfill(orderId);

  const showPaymentDueInput =
    !invoiceSent &&
    !invoicePaid &&
    isInvoiceCreationStage &&
    !showSellarCreditFeatures;

  const onConfirm: SubmitHandler<OrderCollectedFormValues> = async (data) => {
    // oxlint-disable-next-line react/purity -- Captured when this deferred submit handler runs.
    const collectedAt = new Date();
    const updatedPaymentDue = data.paymentDue;

    orderFulfillMutation
      .mutateAsync({
        deliveredAt: collectedAt,
        ...(showPaymentDueInput &&
          updatedPaymentDue && {
            paymentDue: new Date(updatedPaymentDue),
          }),
      })
      .then(() => {
        track("Order Marked As Collected", {
          initiator: "supplier",
          orderId: orderId,
          collectedAt,
        });
        onDismiss();
      });
  };

  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <form onSubmit={form.handleSubmit(onConfirm)}>
        <DialogNavigation
          title={"Mark this order as collected"}
          description={
            "Completing this action will notify the customer and mark the order as collected. This cannot be undone."
          }
          onClickLeftIcon={() => onDismiss()}
          leftIcon={"CHEVRON_DOWN"}
        />
        <DialogContent>
          {showPaymentDueInput && (
            <OrderPaymentDueFormFragment retailerId={retailerId} form={form} />
          )}
        </DialogContent>
        <DialogCTA>
          <Button type={"submit"} loading={orderFulfillMutation.isPending}>
            Order collected
          </Button>
        </DialogCTA>
      </form>
    </Dialog>
  );
}
